// modules/social-accounts/useAccounts.ts
// Hook: список акаунтів, статуси, delete

import { useState, useEffect, useCallback } from 'react';
import { api, ApiClientError } from '../../core/apiClient.ts';
import type { SocialAccount } from '../../core/types.ts';

interface UseAccountsReturn {
  accounts: SocialAccount[];
  loading: boolean;
  error: string | null;
  fetchAccounts: () => Promise<void>;
  deleteAccount: (id: string) => Promise<boolean>;
}

export function useAccounts(): UseAccountsReturn {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<SocialAccount[]>('/api/accounts');
      setAccounts(data);
    } catch (err) {
      const message = err instanceof ApiClientError
        ? err.message
        : 'Не вдалося завантажити акаунти';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAccount = useCallback(async (id: string): Promise<boolean> => {
    try {
      await api.delete(`/api/accounts/${id}`);
      setAccounts((prev) => prev.filter((a) => a.id !== id));
      return true;
    } catch (err) {
      const message = err instanceof ApiClientError
        ? err.message
        : 'Не вдалося видалити акаунт';
      setError(message);
      return false;
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
    window.addEventListener('accounts-updated', fetchAccounts);
    return () => window.removeEventListener('accounts-updated', fetchAccounts);
  }, [fetchAccounts]);

  return { accounts, loading, error, fetchAccounts, deleteAccount };
}
